import React, { useState } from "react";
import "./AgencyProfileLogo.scss";
import {
  Typography,
  IconButton,
  Box,
  Avatar,
  CircularProgress,
} from "@mui/material";
import { toast } from "react-hot-toast";
import { AgencyApi } from "../../../utils/api/Agency";
import { getCdnImageUrl, getImageUrl } from "../../../utils/helpers/common";
import Upload from "../../../components/icons/Upload";

interface AgencyProfileLogoProps {
  formik: any;
  useOfficeLogo?: boolean;
  agencyId?: string;
  onColorsGenerated?: (colors: {
    base_color_primary: any;
    base_color_secondary: any;
    text_color_primary: any;
    text_color_secondary: any;
  }) => void;
  onUploadComplete?: () => Promise<void> | void;
}

const AgencyProfileLogo = ({
  formik,
  useOfficeLogo,
  agencyId,
  onColorsGenerated,
  onUploadComplete,
}: AgencyProfileLogoProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const [state, setState] = useState({
    isLoadingUpload: false,
  });

  const normalizeColor = (color: any) => {
    if (!color) return null;

    if (
      color?.rgb?.r !== undefined &&
      color?.rgb?.g !== undefined &&
      color?.rgb?.b !== undefined
    ) {
      return color;
    }

    if (
      color?.r !== undefined &&
      color?.g !== undefined &&
      color?.b !== undefined
    ) {
      return {
        r: color.r,
        g: color.g,
        b: color.b,
        hex:
          color.hex ||
          `#${componentToHex(color.r)}${componentToHex(color.g)}${componentToHex(color.b)}`,
      };
    }

    if (color?.hex) {
      const hex = color.hex.startsWith("#") ? color.hex : `#${color.hex}`;
      return {
        r: parseInt(hex.slice(1, 3), 16),
        g: parseInt(hex.slice(3, 5), 16),
        b: parseInt(hex.slice(5, 7), 16),
        hex: hex.toUpperCase(),
      };
    }

    return null;
  };

  function componentToHex(c: number) {
    const hex = c.toString(16);
    return hex.length === 1 ? "0" + hex : hex;
  }

  const getLogoUrl = (uploadRecord: any) => {
    if (!uploadRecord) return "";

    if (uploadRecord.storage_key) {
      return getCdnImageUrl(uploadRecord.storage_key);
    }

    if (uploadRecord.original_file_path) {
      return getImageUrl(uploadRecord.original_file_path);
    }

    return "";
  };

  const handleImageChange = async (file: File) => {
    if (!file) {
      toast.error("Please select a valid image file!");
      return;
    }

    const formData = new FormData();
    formData.append("agency_logo", file);
    formData.append("logo_preference", "office_logo");
    formData.append("use_office_logo", "true");

    if (agencyId) {
      formData.append("agency_id", agencyId);
    } else {
      toast.error("Agency ID missing");
      return;
    }

    setState({ ...state, isLoadingUpload: true });

    try {
      const res = await AgencyApi.AgencyLogo(formData);

      if (res?.data?.success) {
        const responseData = res.data.data;
        const uploadRecord = responseData.upload_record;

        // formik.setFieldValue(
        //   "profileLogo",
        //   responseData.upload_record?.original_file_path,
        // );

        formik.setFieldValue(
          "profileLogo",
          uploadRecord?._id || uploadRecord?.original_file_path,
        );

        formik.setFieldValue("profileLogoUploadRecord", uploadRecord);

        formik.setFieldValue(
          "agency_base_color_primary",
          normalizeColor(responseData.base_color_primary),
        );
        formik.setFieldValue(
          "agency_base_color_secondary",
          normalizeColor(responseData.base_color_secondary),
        );
        formik.setFieldValue(
          "agency_text_color_primary",
          normalizeColor(responseData.text_color_primary),
        );
        formik.setFieldValue(
          "agency_text_color_secondary",
          normalizeColor(responseData.text_color_secondary),
        );

        if (responseData.theme_colors) {
          formik.setFieldValue(
            "agency_theme_colors",
            responseData.theme_colors,
          );
        }

        onColorsGenerated?.({
          base_color_primary: normalizeColor(responseData.base_color_primary),
          base_color_secondary: normalizeColor(
            responseData.base_color_secondary,
          ),
          text_color_primary: normalizeColor(responseData.text_color_primary),
          text_color_secondary: normalizeColor(
            responseData.text_color_secondary,
          ),
        });

        if (useOfficeLogo && agencyId) {
          await AgencyApi.updateAgencyProfile(
            {
              logo_preference: "office_logo",
              use_office_logo: true,
              automatic_timezone: true,
            },
            agencyId,
          );
        }

        if (onUploadComplete) {
          try {
            await onUploadComplete();
          } catch (err) {
            console.error("onUploadComplete failed:", err);
          }
        }
      } else {
        toast.error(res?.data?.message || "Failed to upload logo");
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("An error occurred while uploading the logo");
    } finally {
      setState({ ...state, isLoadingUpload: false });
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleImageChange(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDragEnter = () => setIsDragging(true);
  const handleDragLeave = () => setIsDragging(false);

  const getLogoDisplayUrl = () => {
    const uploadRecord = formik.values.profileLogoUploadRecord;

    if (uploadRecord) {
      return getLogoUrl(uploadRecord);
    }

    if (typeof formik.values.profileLogo === "string") {
      return getImageUrl(formik.values.profileLogo);
    }

    return "";
  };

  return (
    <div className="agency-profile-logo-container">
      {formik.values.profileLogo ? (
        <Box>
          <div className="profile-image-container">
            <img
              // src={getImageUrl(formik.values.profileLogo)}
              src={getLogoDisplayUrl()}
              alt="Profile Picture"
            />
          </div>
          <Box mt={2} display="flex" gap={2}>
            <button
              className="agency-update-button"
              disabled={state.isLoadingUpload}
            >
              {state.isLoadingUpload ? (
                <div className="black-color-circular-progress">
                  <CircularProgress size={23} />
                </div>
              ) : (
                "Change logo"
              )}
              <input
                type="file"
                disabled={state.isLoadingUpload}
                onChange={(e) =>
                  e.target.files && handleImageChange(e.target.files[0])
                }
              />
            </button>
          </Box>
        </Box>
      ) : (
        <Box
          className={`profile-picture-upload ${isDragging ? "dragging" : ""}`}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <Box
            component="label"
            className={`upload-box ${formik?.errors?.profileLogo ? "upload-box-error" : ""}`}
          >
            <input
              type="file"
              hidden
              onChange={(e) =>
                e.target.files && handleImageChange(e.target.files[0])
              }
              disabled={state.isLoadingUpload}
            />
            <Box className="upload-content">
              <IconButton
                aria-label="upload"
                color="default"
                size="large"
                className="upload-icon"
              >
                <div className="upload-icon-box">
                  {state.isLoadingUpload ? (
                    <div className="black-color-circular-progress">
                      <CircularProgress size={23} />
                    </div>
                  ) : (
                    <Upload />
                  )}
                </div>
              </IconButton>
              <Typography variant="body2" color="textSecondary">
                {isDragging ? "Release to Upload" : "Drop or Select file"}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </div>
  );
};

export default AgencyProfileLogo;
